import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';

import {
  achievementLabel,
  outcomeMeta,
  requirementLabel,
} from '~/components/visitsCompletion/visitsCompletionState';
import { OutcomeEnum, ResolutionEnum } from '~/data/generated/model';
import type {
  MaintenanceVisitHistoryEntry,
  MaintenanceVisitPeriod,
  MaintenanceVisitTicket,
} from '~/data/schemas/maintenanceVisit';
import { BUTTON_SX } from '~/theme';
import { buildUrlForTickets } from '~/utils/urlBuilder';

// Said plainly rather than hedged: the number is real, it just measures something
// wider than time on site.
const DERIVED_HOURS_NOTE =
  'No on-site time was reported for these tickets, so this is the gap between the start ' +
  'and finish of each visit, which can include travel and breaks.';

const DERIVED_TICKET_NOTE =
  'This ticket reported no on-site time, so its hours are the gap between its start and ' +
  'finish, which can include travel and breaks.';

/** Marks an hours figure that was inferred rather than reported, and says why. */
function DerivedMark({ note }: { note: string }) {
  return (
    <Tooltip title={note} describeChild>
      {/* Focusable so the explanation is reachable without a pointer: the figure it
          qualifies cannot be read correctly without it. */}
      <Box
        component="span"
        tabIndex={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          ml: 0.75,
          color: 'text.secondary',
          cursor: 'help',
          verticalAlign: 'middle',
        }}
      >
        <Typography component="span" variant="caption">
          derived
        </Typography>
        <InfoOutlinedIcon sx={{ fontSize: 14 }} titleAccess="What derived means" />
      </Box>
    </Tooltip>
  );
}

const asDate = (value: string) => format(parseISO(value), 'd MMM yyyy');
const asDateTime = (value: string) => format(parseISO(value), 'd MMM yyyy, HH:mm');

const HISTORY_LABELS: Record<
  MaintenanceVisitHistoryEntry['kind'],
  { label: string; color: string }
> = {
  marked_not_performed: { label: 'Marked as not performed', color: 'error.main' },
  removed_not_performed: { label: 'Not performed removed', color: 'text.secondary' },
  record_received: { label: 'Maintenance record reached us', color: 'success.main' },
};

// `parseApiResponse` is report-only, so an entry kind the API added ahead of this
// build arrives as raw data. An unguarded lookup would throw and take the whole
// modal down over one unrecognised row.
const historyLabel = (kind: MaintenanceVisitHistoryEntry['kind']) =>
  HISTORY_LABELS[kind] ?? { label: 'Activity', color: 'text.secondary' };

const periodRange = (period: MaintenanceVisitPeriod) =>
  period.periodMonths === 1
    ? format(parseISO(period.periodStart), 'MMMM yyyy')
    : `${format(parseISO(period.periodStart), 'MMM')} to ${format(
        parseISO(period.periodEnd),
        'MMM yyyy',
      )}`;

export function PeriodDetailModal({
  period,
  onClose,
  onMarkNotPerformed,
  onClearNotPerformed,
  canWrite,
  busy = false,
  loading = false,
}: {
  period: MaintenanceVisitPeriod | null;
  onClose: () => void;
  onMarkNotPerformed: (period: MaintenanceVisitPeriod) => void;
  onClearNotPerformed: (period: MaintenanceVisitPeriod) => void;
  canWrite: boolean;
  busy?: boolean;
  loading?: boolean;
}) {
  const closeAll = onClose;
  // Nothing to assert about a period the records already account for: the marks stay
  // in the history, and the banner explains a stale one.
  const settled = period?.outcome === OutcomeEnum.done;
  // Keyed on the records themselves as well as what they add up to: an hours ticket
  // worth no time on site scores zero, and hiding it here left the history below
  // listing a record this section said had never reached us.
  const hasRecords = !!period && ((period.tickets ?? []).length > 0 || period.achievedQuantity > 0);
  // Only claimable where nothing at all reached us. `missing` carries most of the
  // conditions, since done, skipped and open all outrank it, but not the tickets: an
  // hours ticket worth zero still leaves the period reading `missing`, and the server
  // refuses that. Offering the button there would only ever produce an error.
  const canMarkNotPerformed = canWrite && period?.outcome === OutcomeEnum.missing && !hasRecords;

  return (
    <Dialog open={period !== null || loading} onClose={closeAll} maxWidth="sm" fullWidth>
      {period === null && loading ? (
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </DialogContent>
      ) : null}
      {period ? (
        <>
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <Stack>
              <Typography variant="h6" component="span">
                {period.carName} · {periodRange(period)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[period.propertyName, period.serviceProviderName].filter(Boolean).join(' · ')}
              </Typography>
            </Stack>
            <IconButton onClick={closeAll} aria-label="Close" sx={{ mt: -0.5, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <StateHeading period={period} />

            <Section title="What the contract expects">
              <Field label="Requirement" value={requirementLabel(period)} />
              <Field
                label="Period"
                value={`${asDate(period.periodStart)} to ${asDate(period.periodEnd)}`}
              />
              {period.serviceProviderName ? (
                <Field label="Service provider" value={period.serviceProviderName} />
              ) : null}
            </Section>

            <Divider sx={{ my: 2 }} />

            <Section title="Records we hold">
              {hasRecords ? (
                <>
                  <Field
                    label={period.requiredUnit === 'hours' ? 'Hours recorded' : 'Visits recorded'}
                    value={`${period.achievedQuantity} of ${period.requiredQuantity}`}
                    note={period.hoursDerived ? DERIVED_HOURS_NOTE : undefined}
                  />
                  {(period.tickets ?? []).length > 0 ? (
                    <TicketLinks
                      tickets={period.tickets ?? []}
                      showHours={period.requiredUnit === 'hours'}
                    />
                  ) : null}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No maintenance record has reached us for this period.
                </Typography>
              )}
            </Section>

            {(period.history ?? []).length > 0 ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Section title="History">
                  <PeriodTimeline entries={period.history ?? []} />
                </Section>
              </>
            ) : null}

            {period.contested ? <ContestedNotice /> : null}
          </DialogContent>

          <DialogActions
            sx={{ px: 3, py: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}
          >
            {/* Close first, so the action that changes something sits on the right
                where it is reached last. */}
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button onClick={closeAll} variant="outlined" sx={BUTTON_SX}>
                Close
              </Button>
              {!canWrite || settled ? null : period.resolution === ResolutionEnum.not_performed ? (
                <Button
                  onClick={() => onClearNotPerformed(period)}
                  loading={busy}
                  variant="outlined"
                  sx={BUTTON_SX}
                >
                  Remove not performed
                </Button>
              ) : null}
              {canMarkNotPerformed ? (
                <Button
                  onClick={() => onMarkNotPerformed(period)}
                  loading={busy}
                  variant="outlined"
                  color="error"
                  sx={BUTTON_SX}
                >
                  Mark as not performed
                </Button>
              ) : null}
            </Stack>
            {/* Helper text under the button it belongs to, a line per point, so neither
                competes with the action for attention. */}
            {canMarkNotPerformed ? (
              <Stack sx={{ textAlign: 'right' }}>
                {[
                  'You can undo this at any time.',
                  'A maintenance record arriving later replaces it.',
                ].map((line) => (
                  <Typography
                    key={line}
                    variant="caption"
                    color="text.secondary"
                    fontStyle="italic"
                  >
                    {line}
                  </Typography>
                ))}
              </Stack>
            ) : null}
          </DialogActions>
        </>
      ) : null}
    </Dialog>
  );
}

function ContestedNotice() {
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      <AlertTitle>A record arrived after this was marked</AlertTitle>A maintenance record reached us
      for this period after it was marked as not performed, so it no longer counts as a skipped
      visit. Nothing to do. The mark is kept in the history below.
    </Alert>
  );
}

function StateHeading({ period }: { period: MaintenanceVisitPeriod }) {
  const meta = outcomeMeta(period.outcome);
  const shortfall = achievementLabel(period);
  return (
    <Stack gap={0.5} sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <meta.Icon sx={{ fontSize: 22, color: meta.color }} />
        <Typography variant="subtitle1" fontWeight={600}>
          {meta.label}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {meta.description}
      </Typography>
      {shortfall ? (
        <Typography variant="body2" color="text.secondary">
          {shortfall} {period.requiredUnit === 'hours' ? 'hours' : 'visits'} recorded.
        </Typography>
      ) : null}
    </Stack>
  );
}

function PeriodTimeline({ entries }: { entries: readonly MaintenanceVisitHistoryEntry[] }) {
  return (
    <Stack gap={1.5}>
      {entries.map((event) => {
        const meta = historyLabel(event.kind);
        return (
          <Stack key={event.id} direction="row" gap={1.5}>
            <Stack
              sx={{
                width: 8,
                height: 8,
                mt: 0.75,
                flex: '0 0 auto',
                borderRadius: '50%',
                bgcolor: meta.color,
              }}
            />
            <Stack gap={0.25}>
              <Typography variant="body2" fontWeight={600}>
                {meta.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {event.actor ? `${event.actor} · ` : ''}
                {asDateTime(event.at)}
              </Typography>
              {event.note ? (
                <Typography variant="body2" color="text.secondary">
                  {event.note}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}

function TicketLinks({
  tickets,
  showHours,
}: {
  tickets: readonly MaintenanceVisitTicket[];
  showHours: boolean;
}) {
  return (
    <Stack direction="row" gap={2}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 140px' }}>
        {tickets.length === 1 ? 'Ticket' : 'Tickets'}
      </Typography>
      <Stack gap={0.5}>
        {tickets.map((ticket) => (
          <Stack key={ticket.id} direction="row" alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Link
              href={buildUrlForTickets({ number: ticket.number })}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
            >
              {ticket.number || 'Untitled ticket'}
              <Typography component="span" variant="caption" color="text.secondary">
                {asDate(ticket.visitedOn)}
                {showHours ? ` · ${ticket.hours} ${ticket.hours === 1 ? 'hour' : 'hours'}` : ''}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
            {showHours && ticket.hoursDerived ? <DerivedMark note={DERIVED_TICKET_NOTE} /> : null}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Stack gap={1}>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function Field({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Stack direction="row" gap={2}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: '0 0 140px' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="span">
        {value}
        {note ? <DerivedMark note={note} /> : null}
      </Typography>
    </Stack>
  );
}
